import { type ApiSchemas, call } from '@rental/contracts/client'
import { LOCALES } from '@rental/domain'
import { format, formatDateTime } from '@rental/i18n'
import { Button, cn, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ReasonList, SubPanel } from '../components/SubPanel'
import {
  FIELD,
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  ProblemNotice,
  SHORT_FIELD,
  StaffInput,
  StaffSelect,
  StaffTextarea,
} from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { problemOf, type StaffProblem } from '../problems'
import {
  type Based,
  CONTACT_FIELDS,
  CUSTOMER_FIELDS,
  type CustomerDraft,
  type CustomerField,
  customerCorrection,
  customerDraftOf,
  invalidCustomerFields,
  PARTY_TYPES,
  rebase,
  refusedFields,
} from './correction'
import { customerQueryKey, loadCustomer } from './queries'

type Customer = ApiSchemas['CustomerDetail']['customer']

/** How a correction ended, for the record's page to say. */
export type CorrectionOutcome =
  | { kind: 'saved'; fields: readonly string[] }
  | { kind: 'unchanged' }
  /** The record cannot take it any more (anonymised meanwhile): said, and the form gone. */
  | { kind: 'refused'; problem: StaffProblem }
  /** A licence erased with its customer's identity meanwhile: nothing of it is left to correct. */
  | { kind: 'gone' }

const ANONYMISED: StaffProblem = {
  kind: 'api',
  status: 409,
  code: 'customer_anonymised',
  details: {},
}

/**
 * S8 (GDPR art. 16), "Modifica dati": the customer's details put right, by
 * whoever holds customers.write (the Owner and the Agent). The form starts
 * from the record as read and sends only what was changed, with the version
 * read: a record changed meanwhile is refused (nothing saved), and read
 * again without losing what was typed. An erased contact is never offered
 * back. Signed contracts and what CaRGOS received keep what they recorded.
 */
export function CustomerCorrectionForm({
  customer,
  onDone,
  onCancel,
}: {
  customer: Customer
  onDone: (outcome: CorrectionOutcome) => void
  onCancel: () => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.customer.correction
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const titleId = useId()
  const reasonsId = useId()
  const firstField = useRef<HTMLInputElement>(null)
  const rereadAction = useRef<HTMLDivElement>(null)
  const saveAction = useRef<HTMLDivElement>(null)
  /** Where the focus goes once the form has drawn what an answer changed. */
  const focusNext = useRef<'reread' | 'save' | null>(null)
  const [base, setBase] = useState<Based<CustomerDraft>>(() => customerDraftOf(customer))
  const [draft, setDraft] = useState<CustomerDraft>(() => customerDraftOf(customer).values)
  const [erasedAt, setErasedAt] = useState(customer.contactErasedAt)
  const [busy, setBusy] = useState<'saving' | 'reading' | null>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [refused, setRefused] = useState<CustomerField[]>([])
  const [conflict, setConflict] = useState(false)
  const [reread, setReread] = useState<{
    kept: CustomerField[]
    clashed: CustomerField[]
  } | null>(null)

  // The form takes the place of the button that opened it: the focus goes to its first field.
  useEffect(() => {
    firstField.current?.focus()
  }, [])

  // A refusal disables the button that was pressed, and a read again takes the way to read
  // away: the focus moves on to what the counter does next, never to nothing (UI-2g).
  useEffect(() => {
    const next = focusNext.current
    if (!next) return
    focusNext.current = null
    const button = (next === 'reread' ? rereadAction : saveAction).current?.querySelector('button')
    if (button && !button.disabled) button.focus()
    else firstField.current?.focus()
  })

  const contactErased = erasedAt !== null
  const invalid = invalidCustomerFields(draft)
  const { changed } = customerCorrection(base, draft)
  const reasons = busy
    ? []
    : conflict
      ? [copy.why.conflict]
      : changed.length === 0
        ? [copy.why.nothing]
        : invalid.length > 0
          ? [copy.why.invalid]
          : []
  const names = (fields: readonly CustomerField[]) =>
    fields.map((field) => copy.fields[field]).join(', ')

  function set(field: CustomerField, value: string) {
    setDraft((current) => ({ ...current, [field]: value }))
    // What the server named is said until the field is typed in again.
    setRefused((current) => current.filter((name) => name !== field))
  }

  /** The record as it is now, and the form moved onto it: what was typed stays. */
  async function readAgain(): Promise<boolean> {
    setBusy('reading')
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: customerQueryKey(customer.id),
        queryFn: () => loadCustomer(customer.id),
        staleTime: 0,
      })
      if (fresh.customer.anonymisedAt !== null) {
        onDone({ kind: 'refused', problem: ANONYMISED })
        return false
      }
      const next = rebase(
        base,
        draft,
        customerDraftOf(fresh.customer),
        fresh.customer.contactErasedAt === null ? [] : CONTACT_FIELDS,
      )
      setBase(next.base)
      setDraft(next.draft)
      setErasedAt(fresh.customer.contactErasedAt)
      setConflict(false)
      setReread({ kept: next.kept, clashed: next.clashed })
      focusNext.current = 'save'
      return true
    } catch (error) {
      setProblem(problemOf(error))
      return false
    } finally {
      setBusy(null)
    }
  }

  async function save() {
    if (busy || reasons.length > 0) return
    const { body } = customerCorrection(base, draft)
    setProblem(null)
    setRefused([])
    setReread(null)
    setBusy('saving')
    const result = await call(
      api.PATCH('/v1/staff/customers/{customerId}', {
        params: { path: { customerId: customer.id } },
        body,
      }),
    )
    setBusy(null)
    if (result.ok) {
      onDone(
        result.data.changed.length === 0
          ? { kind: 'unchanged' }
          : {
              kind: 'saved',
              fields: result.data.changed.map((field) => copy.fields[field]),
            },
      )
      return
    }
    if (takeOver(result.problem)) return
    const code = result.problem.kind === 'api' ? result.problem.code : null
    if (code === 'version_conflict') {
      setConflict(true)
      focusNext.current = 'reread'
      return
    }
    if (code === 'customer_anonymised') {
      onDone({ kind: 'refused', problem: result.problem })
      return
    }
    if (code === 'contact_erased') {
      // Erased meanwhile: the form loses the contact fields, and keeps the rest.
      if (await readAgain()) setProblem(result.problem)
      return
    }
    setRefused(refusedFields(result.problem, CUSTOMER_FIELDS))
    setProblem(result.problem)
  }

  const errorOf = (field: CustomerField) =>
    invalid.includes(field) || refused.includes(field) ? copy.invalid : null

  const input = (
    field: CustomerField,
    options: {
      className: string
      required?: boolean
      hint?: ReactNode
      type?: string
      maxLength?: number
      upper?: boolean
      autoComplete?: string
    },
  ) => (
    <Field
      className={options.className}
      label={copy[field]}
      hint={options.hint}
      required={options.required === true}
      {...(options.required ? {} : { optionalLabel: copy.optional })}
      error={errorOf(field)}
    >
      {(control) => (
        <StaffInput
          {...control}
          {...(field === 'firstName' ? { ref: firstField } : {})}
          type={options.type ?? 'text'}
          autoComplete={options.autoComplete ?? 'off'}
          {...(options.maxLength ? { maxLength: options.maxLength } : {})}
          {...(options.upper ? { autoCapitalize: 'characters' } : {})}
          value={draft[field]}
          onChange={(event) =>
            set(field, options.upper ? event.target.value.toUpperCase() : event.target.value)
          }
        />
      )}
    </Field>
  )

  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        void save()
      }}
      onKeyDown={noImplicitSubmit}
      aria-labelledby={titleId}
      noValidate
    >
      <SubPanel title={<span id={titleId}>{copy.title}</span>}>
        <div className={FIELD_ROW}>
          {input('firstName', {
            className: FIELD,
            required: true,
            maxLength: 80,
          })}
          {input('lastName', {
            className: FIELD,
            required: true,
            maxLength: 80,
          })}
        </div>
        <div className={cn(FIELD_ROW, 'items-end')}>
          <FieldPair>
            {input('birthDate', { className: PAIRED_FIELD, type: 'date' })}
            {input('nationality', {
              className: PAIRED_FIELD,
              maxLength: 2,
              upper: true,
            })}
          </FieldPair>
          {input('birthPlace', { className: FIELD, maxLength: 100 })}
        </div>
        {erasedAt !== null ? (
          <Notice tone="info">
            <p>
              {format(copy.contactErased, {
                date: formatDateTime(locale, erasedAt),
              })}
            </p>
          </Notice>
        ) : (
          <div className={FIELD_ROW}>
            {input('phone', {
              className: FIELD,
              type: 'tel',
              hint: copy.phoneHint,
            })}
            {input('email', {
              className: FIELD,
              type: 'email',
              maxLength: 254,
            })}
          </div>
        )}
        <div className={FIELD_ROW}>
          {input('addressLine', { className: FIELD, maxLength: 200 })}
        </div>
        <div className={FIELD_ROW}>
          {input('postalCode', { className: SHORT_FIELD, maxLength: 12 })}
          {input('city', { className: FIELD, maxLength: 100 })}
          {input('province', {
            className: SHORT_FIELD,
            maxLength: 8,
            upper: true,
          })}
          {input('country', {
            className: SHORT_FIELD,
            maxLength: 2,
            upper: true,
          })}
        </div>
        <div className={FIELD_ROW}>
          <Field label={copy.preferredLocale} className={cn(FIELD, 'max-w-xs')}>
            {(control) => (
              <StaffSelect
                {...control}
                value={draft.preferredLocale}
                onChange={(event) => set('preferredLocale', event.target.value)}
              >
                {LOCALES.map((value) => (
                  <option key={value} value={value}>
                    {m.languages[value]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
        </div>
        <p className="text-sm font-medium text-text-muted">{copy.fiscal}</p>
        <div className={FIELD_ROW}>
          <Field
            label={copy.partyType}
            optionalLabel={copy.optional}
            className={cn(FIELD, 'max-w-xs')}
            error={errorOf('partyType')}
          >
            {(control) => (
              <StaffSelect
                {...control}
                value={draft.partyType}
                onChange={(event) => set('partyType', event.target.value)}
              >
                <option value="">{copy.partyTypeNone}</option>
                {PARTY_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {copy.partyTypes[value]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
        </div>
        <div className={FIELD_ROW}>
          {input('vatNumber', { className: FIELD, maxLength: 20 })}
          {input('taxCode', { className: FIELD, maxLength: 32 })}
        </div>
        <div className={FIELD_ROW}>
          {input('sdiCode', { className: SHORT_FIELD, maxLength: 10 })}
          {contactErased ? null : input('pec', { className: FIELD, type: 'email', maxLength: 254 })}
        </div>
        {contactErased ? null : (
          <Field label={copy.notes} optionalLabel={copy.optional} error={errorOf('notes')}>
            {(control) => (
              <StaffTextarea
                {...control}
                maxLength={500}
                value={draft.notes}
                onChange={(event) => set('notes', event.target.value)}
              />
            )}
          </Field>
        )}
        <p className="text-sm text-text-muted">{copy.keepsRecords}</p>
        {conflict ? (
          <>
            <Notice tone="warning" live="assertive">
              <p>{copy.conflict}</p>
            </Notice>
            <div ref={rereadAction}>
              <Button
                variant="outline"
                className="rounded-sm"
                busy={busy === 'reading'}
                onClick={() => void readAgain()}
              >
                {copy.reread}
              </Button>
            </div>
          </>
        ) : null}
        {reread ? (
          <Notice tone={reread.clashed.length > 0 ? 'warning' : 'info'} live="polite">
            <p>
              {reread.kept.length > 0
                ? format(copy.rereadKept, { fields: names(reread.kept) })
                : copy.rereadNone}
            </p>
            {reread.clashed.length > 0 ? (
              <p>{format(copy.clashed, { fields: names(reread.clashed) })}</p>
            ) : null}
          </Notice>
        ) : null}
        <ProblemNotice problem={problem} />
        <div ref={saveAction} className="flex flex-wrap gap-3">
          <Button
            type="submit"
            variant="staff"
            busy={busy === 'saving'}
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {copy.save}
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            disabled={busy === 'saving'}
            onClick={onCancel}
          >
            {copy.cancel}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </SubPanel>
    </form>
  )
}
